'use client';

import { fetchStoreReviews, StoreReview } from '@eliadmin/core/src/api/general/general-api';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Paragraph } from '@eliadmin/core/src/components/ui/paragraph';
import { SectionHeading } from '@eliadmin/core/src/components/ui/section-heading';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useQuery } from '@tanstack/react-query';
import Image from 'next/image';
import Link from 'next/link';
import { useMemo } from 'react';
import { IoMdStar } from 'react-icons/io';

export function Reviews({
  subtitle,
  title,
  description,
  showVector = true,
}: {
  subtitle: string;
  title: string;
  description: string;
  showVector?: boolean;
}) {
  const locale = useLocaleStore(s => s.currentLocale);

  const { data: reviews = { data: [] }, isLoading } = useQuery({
    queryKey: ['REVIEWS', locale],
    queryFn: () => fetchStoreReviews({ locale, query: { page: 1, results: 10 } }),
  });

  const track = useMemo(() => {
    const items: React.ReactNode[] = [];

    for (let setIndex = 0; setIndex < 2; setIndex++) {
      reviews.data.forEach((review, reviewIndex) => {
        items.push(<ReviewCard key={`${setIndex}-${reviewIndex}`} review={review} />);
      });
      items.push(<CtaCard key={`${setIndex}-cta`} />);
    }

    return items;
  }, [reviews.data]);

  return (
    <section
      className={cn(
        'custom-section',
        'relative bg-white px-0 pb-5 sm:pb-8 md:pb-10 lg:pb-[50px]',
        showVector ? ' pt-16 sm:pt-24 md:pt-40 lg:pt-[200px]' : 'pt-8 sm:pt-12',
      )}
    >
      {!!showVector && (
        <Image
          src={'/images/subpage-header-vector.svg'}
          alt="Revews"
          width={0}
          height={0}
          className="absolute left-0 top-0 z-10 h-auto w-full"
        />
      )}

      <SectionHeading
        align="center"
        subtitle={subtitle}
        title={title}
        description={description}
        className={{ description: 'w-full sm:max-w-[568px]' }}
      />
      <div
        className={cn(
          'ea-reviews-carousel',
          'group relative flex w-full overflow-hidden py-6 sm:py-8 md:py-10 lg:py-[50px]',
          '[mask-image:linear-gradient(to_right,transparent,black_5%,black_95%,transparent)]',
        )}
      >
        {isLoading ? (
          <div className="flex w-full items-center justify-center py-10">
            <Spinner size="lg" />
          </div>
        ) : (
          <div
            className={cn(
              'ea-reviews-carousel__track',
              'flex w-max animate-marquee items-stretch gap-3 will-change-transform sm:gap-4 lg:gap-6',
              'group-hover:[animation-play-state:paused]',
              'motion-reduce:animate-none',
            )}
          >
            {track}
          </div>
        )}
      </div>
      <Button asChild variant={'primary-foreground'} className="w-full sm:w-fit">
        <Link prefetch={false} href={'#'} target="_blank">
          Všetkých 1350+ recenzií na Heureka.sk
        </Link>
      </Button>
    </section>
  );
}

function ReviewCard({ review }: { review: StoreReview }) {
  return (
    <div
      className={cn(
        'ea-review-card',
        'flex h-[300px] w-[204px] flex-shrink-0 flex-col items-center justify-between rounded-[12px] p-4 sm:h-[320px] sm:w-[230px] sm:p-5 lg:h-[340px] lg:w-[282px] lg:p-6',
      )}
    >
      <div className="flex w-full flex-col items-center justify-center gap-3">
        <div className="flex items-center justify-center gap-[2px]">
          {[...Array(5)].map((_, index) => (
            <IoMdStar key={index} size={32} className="text-[#F5B42F]" />
          ))}
        </div>
        <Paragraph
          size="base"
          color="foreground"
          className="line-clamp-6 w-full text-center"
          innerHtml={review.review}
        />
      </div>
      <div className="flex w-full flex-col items-start justify-start gap-1">
        <Paragraph size="sm" color="foreground" className="font-semibold">
          {review.name}
        </Paragraph>
      </div>
    </div>
  );
}

function CtaCard() {
  return (
    <div
      className={cn(
        'ea-review-card',
        'flex h-[300px] w-[204px] flex-shrink-0 flex-col items-center justify-center gap-6 rounded-[12px] p-4 sm:h-[320px] sm:w-[230px] sm:p-5 lg:h-[340px] lg:w-[282px] lg:p-6',
      )}
    >
      <div data-ea-visual-tag="h2" className={cn('h1', 'font-semibold -tracking-tighter text-foreground')}>4,8</div>
      <div className="flex items-center justify-center gap-[2px]">
        {[...Array(5)].map((_, index) => (
          <IoMdStar key={index} size={32} className="text-[#F5B42F]" />
        ))}
      </div>
      <p className="w-full text-center text-lg text-foreground sm:text-xl">1 350+ recenzií</p>
    </div>
  );
}
